import Cropper from "react-easy-crop";
import type { Area } from "react-easy-crop";
import { Button, Alert, SuccessNotice } from "@a2z/ui";
import { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import { useCreateStoreLogoUploadIntentMutation } from "@/modules/settings/services/useCreateStoreLogoUploadIntentMutation";
import { useUpdateStoreMutation } from "@/modules/settings/services/useUpdateStoreMutation";
import { useUploadToSignedUrlMutation } from "@/shared/services/useUploadToSignedUrlMutation";

type Props = {
  logoUrl: string | null;
};

const ACCEPTED_TYPES = ["image/jpeg", "image/png", "image/webp"] as const;
const MAX_STORE_LOGO_BYTES = 1024 * 1024;
const STORE_LOGO_CANVAS_SIZE_PX = 512;

type PreparedLogo = {
  blob: Blob;
  contentType: string;
  fileName: string;
};

/** "idle" → no file selected; "cropping" → crop UI shown; "previewing" → crop confirmed, ready to upload */
type Stage = "idle" | "cropping" | "previewing";

const contentTypeFromFile = (file: File): string | null => {
  return ACCEPTED_TYPES.some((acceptedType) => acceptedType === file.type)
    ? file.type
    : null;
};

const extensionFromContentType = (contentType: string): string => {
  if (contentType === "image/webp") return "webp";
  if (contentType === "image/png") return "png";
  if (contentType === "image/jpeg") return "jpg";
  return "bin";
};

const loadImageFromUrl = async (url: string): Promise<HTMLImageElement> => {
  const image = new window.Image();
  image.decoding = "async";
  image.src = url;
  await new Promise<void>((resolve, reject) => {
    image.onload = () => resolve();
    image.onerror = () => reject(new Error("Failed to load image"));
  });
  return image;
};

const toBlob = (canvas: HTMLCanvasElement, type: string, quality?: number) => {
  return new Promise<Blob | null>((resolve) => {
    canvas.toBlob((blob) => resolve(blob), type, quality);
  });
};

/**
 * Takes the raw object URL of the original file + the seller-chosen crop area
 * (in original-image pixel coordinates, always square because aspect=1) and
 * produces a compressed, ≤512px, ≤1MB blob ready for upload.
 */
const applyCropAndPrepare = async (
  rawUrl: string,
  cropArea: Area,
): Promise<PreparedLogo> => {
  const image = await loadImageFromUrl(rawUrl);
  const { width: cropWidth, height: cropHeight, x: cropX, y: cropY } = cropArea;

  const side = Math.min(STORE_LOGO_CANVAS_SIZE_PX, Math.round(cropWidth));
  if (side <= 0) throw new Error("invalid");

  const canvas = document.createElement("canvas");
  canvas.width = side;
  canvas.height = side;

  const context = canvas.getContext("2d");
  if (!context) throw new Error("invalid");

  context.drawImage(
    image,
    Math.round(cropX), Math.round(cropY),
    Math.round(cropWidth), Math.round(cropHeight),
    0, 0, side, side,
  );

  let outputType = "image/webp";
  let blob = await toBlob(canvas, outputType, 0.9);

  if (!blob) {
    outputType = "image/jpeg";
    blob = await toBlob(canvas, outputType, 0.9);
  }

  if (!blob) throw new Error("invalid");

  if (blob.size > MAX_STORE_LOGO_BYTES) {
    if (outputType === "image/webp" || outputType === "image/jpeg") {
      for (const quality of [0.82, 0.74, 0.66, 0.58]) {
        const next = await toBlob(canvas, outputType, quality);
        if (next && next.size <= MAX_STORE_LOGO_BYTES) {
          blob = next;
          break;
        }
      }
    }
  }

  if (blob.size > MAX_STORE_LOGO_BYTES) throw new Error("too_large");

  const extension = extensionFromContentType(outputType);
  return { blob, contentType: outputType, fileName: `logo.${extension}` };
};

export function LogoForm({ logoUrl }: Props) {
  const params = useParams<{ storeSlug?: string }>();
  const storeSlug = params?.storeSlug;
  const t = useTranslations();
  const { mutateAsync: updateStore } = useUpdateStoreMutation();
  const { mutateAsync: createLogoUploadIntent } = useCreateStoreLogoUploadIntentMutation();
  const { mutateAsync: uploadToSignedUrl } = useUploadToSignedUrlMutation();
  const inputRef = useRef<HTMLInputElement | null>(null);

  const [error, setError] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [didSucceed, setDidSucceed] = useState(false);
  const [stage, setStage] = useState<Stage>("idle");

  // ── Crop stage state ──────────────────────────────────────────────────────
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [rawImageUrl, setRawImageUrl] = useState<string | null>(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);

  // ── Preview stage state ───────────────────────────────────────────────────
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [preparedLogo, setPreparedLogo] = useState<PreparedLogo | null>(null);
  const [isApplying, setIsApplying] = useState(false);

  // Stable ref so error messages never re-run effects just because `t` changed.
  const tRef = useRef(t);
  useEffect(() => { tRef.current = t; }, [t]);

  // ── Blob URL lifecycle ────────────────────────────────────────────────────
  // Managed via refs so revocation is always decoupled from effect cleanup,
  // preventing the "broken image while React is still rendering it" race.

  const activeBlobUrlRef = useRef<string | null>(null);
  const revokeActiveBlobUrl = () => {
    if (activeBlobUrlRef.current) {
      URL.revokeObjectURL(activeBlobUrlRef.current);
      activeBlobUrlRef.current = null;
    }
  };

  const activeRawUrlRef = useRef<string | null>(null);
  const revokeRawUrl = () => {
    if (activeRawUrlRef.current) {
      URL.revokeObjectURL(activeRawUrlRef.current);
      activeRawUrlRef.current = null;
    }
  };

  // Revoke any remaining URLs on unmount.
  useEffect(() => () => { revokeActiveBlobUrl(); revokeRawUrl(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Helpers ───────────────────────────────────────────────────────────────

  const resetAll = () => {
    revokeRawUrl();
    revokeActiveBlobUrl();
    setRawImageUrl(null);
    setSelectedFile(null);
    setPreviewUrl(null);
    setPreparedLogo(null);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setCroppedAreaPixels(null);
    setStage("idle");
    if (inputRef.current) inputRef.current.value = "";
  };

  // ── File selection → open crop UI ────────────────────────────────────────

  const handleFileSelect = (file: File) => {
    setError(null);
    setDidSucceed(false);

    if (!contentTypeFromFile(file)) {
      setError(t(keys.errors.unsupportedFileType));
      if (inputRef.current) inputRef.current.value = "";
      return;
    }

    revokeRawUrl();
    revokeActiveBlobUrl();
    setPreviewUrl(null);
    setPreparedLogo(null);

    const rawUrl = URL.createObjectURL(file);
    activeRawUrlRef.current = rawUrl;
    setRawImageUrl(rawUrl);
    setSelectedFile(file);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setCroppedAreaPixels(null);
    setStage("cropping");
  };

  const onCropComplete = useCallback((_: Area, pixelArea: Area) => {
    setCroppedAreaPixels(pixelArea);
  }, []);

  // ── Confirm crop → produce preview blob ───────────────────────────────────

  const handleConfirmCrop = async () => {
    if (!rawImageUrl || !croppedAreaPixels) return;
    setIsApplying(true);
    try {
      const prepared = await applyCropAndPrepare(rawImageUrl, croppedAreaPixels);

      revokeActiveBlobUrl();
      const newUrl = URL.createObjectURL(prepared.blob);
      activeBlobUrlRef.current = newUrl;

      setPreparedLogo(prepared);
      setPreviewUrl(newUrl);

      // Raw URL no longer needed — preview blob takes over.
      revokeRawUrl();
      setRawImageUrl(null);
      setStage("previewing");
    } catch (caught) {
      const message =
        caught instanceof Error && caught.message === "too_large"
          ? tRef.current(keys.errors.fileTooLarge)
          : tRef.current(keys.errors.imageProcessingFailed);
      setError(message);
      resetAll();
    } finally {
      setIsApplying(false);
    }
  };

  // ── Go back to crop UI from preview ──────────────────────────────────────

  const handleBackToCrop = () => {
    if (!selectedFile) { resetAll(); return; }
    revokeActiveBlobUrl();
    setPreviewUrl(null);
    setPreparedLogo(null);
    const rawUrl = URL.createObjectURL(selectedFile);
    activeRawUrlRef.current = rawUrl;
    setRawImageUrl(rawUrl);
    setStage("cropping");
  };

  // ── Upload confirmed crop ─────────────────────────────────────────────────

  const persistLogoObjectKey = async (nextLogoObjectKey: string | null) => {
    if (!storeSlug) {
      setError(t(keys.errors.generic));
      return false;
    }
    try {
      await updateStore({
        storeSlug,
        logoObjectKey: nextLogoObjectKey ?? "",
      });
    } catch (caughtError) {
      setError(caughtError instanceof Error ? caughtError.message : t(keys.errors.generic));
      return false;
    }
    return true;
  };

  const handleUpload = async () => {
    if (!preparedLogo) return;
    setError(null);
    setDidSucceed(false);
    setIsUploading(true);
    try {
      const signed = await createLogoUploadIntent({
        fileName: preparedLogo.fileName,
        contentType: preparedLogo.contentType,
        sizeBytes: preparedLogo.blob.size,
      });
      if (!signed.objectKey) {
        setError(t(keys.errors.generic));
        return;
      }
      try {
        await uploadToSignedUrl({
          uploadUrl: signed.uploadUrl,
          file: preparedLogo.blob,
          contentType: preparedLogo.contentType,
        });
      } catch (uploadError) {
        setError(
          uploadError instanceof TypeError
            ? t(keys.errors.uploadBlockedByBrowser)
            : t(keys.products.uploadFailed),
        );
        return;
      }
      // Send the server the object key; the server renders the full URL on the
      // next read of `logoUrl` (server-rendered output).
      const persisted = await persistLogoObjectKey(signed.objectKey);
      if (!persisted) return;
      setDidSucceed(true);
      resetAll();
    } finally {
      setIsUploading(false);
    }
  };

  // ── Remove existing logo ──────────────────────────────────────────────────

  const handleRemove = async () => {
    setError(null);
    setDidSucceed(false);
    setIsUploading(true);
    try {
      const persisted = await persistLogoObjectKey(null);
      if (!persisted) return;
      setDidSucceed(true);
    } finally {
      setIsUploading(false);
    }
  };

  // ─────────────────────────────────────────────────────────────────────────

  const isBusy = isUploading || isApplying;
  const hasImage = Boolean(previewUrl ?? logoUrl);

  return (
    <div className="flex flex-col gap-4">
      {error ? <Alert message={error} helperText={t(keys.errors.recovery)} /> : null}
      {didSucceed ? <SuccessNotice title={t(keys.seller.storeLogoUpdatedNotice)} /> : null}

      {/* Hidden file input */}
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPTED_TYPES.join(",")}
        disabled={isBusy}
        onChange={(event) => {
          const file = event.target.files?.[0];
          if (file) handleFileSelect(file);
        }}
        className="sr-only"
      />

      {stage === "cropping" && rawImageUrl ? (
        /* ── Crop stage ────────────────────────────────────────────────── */
        <div className="flex flex-col gap-4">
          <p className="text-xs text-[color:var(--text-muted)]">
            {t(keys.seller.storeLogoCropHelper)}
          </p>

          {/* Cropper area */}
          <div className="relative h-64 w-full overflow-hidden rounded-2xl bg-black">
            <Cropper
              image={rawImageUrl}
              crop={crop}
              zoom={zoom}
              aspect={1}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
              showGrid={false}
              style={{
                containerStyle: { borderRadius: "1rem" },
                cropAreaStyle: { borderRadius: "0.75rem" },
              }}
            />
          </div>

          {/* Zoom slider */}
          <div className="flex items-center gap-3">
            <span className="shrink-0 text-sm text-[color:var(--text-muted)]">−</span>
            <input
              type="range"
              min={1}
              max={3}
              step={0.01}
              value={zoom}
              onChange={(e) => setZoom(Number(e.target.value))}
              className="h-1 w-full cursor-pointer appearance-none rounded-full bg-[color:var(--border-light)] accent-[color:var(--primary-500)]"
            />
            <span className="shrink-0 text-sm text-[color:var(--text-muted)]">+</span>
          </div>

          {/* Crop action buttons */}
          <div className="flex gap-2">
            <Button
              type="button"
              onClick={() => void handleConfirmCrop()}
              disabled={isApplying || !croppedAreaPixels}
              variant="primary"
              className="h-9 flex-1 rounded-lg text-xs font-semibold disabled:cursor-not-allowed disabled:opacity-60"
            >
              {isApplying ? t(keys.common.loading) : t(keys.seller.storeLogoCropConfirm)}
            </Button>
            <Button
              type="button"
              onClick={resetAll}
              disabled={isApplying}
              variant="outline"
              className="h-9 rounded-lg px-4 text-xs font-semibold disabled:cursor-not-allowed disabled:opacity-60"
            >
              {t(keys.common.cancel)}
            </Button>
          </div>
        </div>
      ) : (
        /* ── Idle / Preview stage ──────────────────────────────────────── */
        <>
          <p className="text-xs text-[color:var(--text-muted)]">
            {t(keys.seller.storeLogoHelper)}
          </p>

          <div className="flex items-center gap-4">
            {/*
             * Logo preview square — tapping it opens the file picker so the
             * seller can replace without needing a separate button.
             */}
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              disabled={isBusy}
              aria-label={t(hasImage ? keys.seller.storeLogoReplace : keys.common.upload)}
              className="relative h-28 w-28 shrink-0 overflow-hidden rounded-2xl border border-[color:var(--border-light)] bg-[color:var(--surface-alt)] transition hover:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--primary-500)] focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-60"
            >
              {previewUrl ?? logoUrl ? (
                <img
                  src={previewUrl ?? logoUrl ?? ""}
                  alt={t(keys.seller.storeLogoTitle)}
                  className="absolute inset-0 h-full w-full object-cover"
                />
              ) : (
                <span className="flex h-full w-full items-center justify-center text-[color:var(--text-muted)]">
                  <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
                    <circle cx="12" cy="13" r="4" />
                  </svg>
                </span>
              )}
              {/* Camera badge — persistent tap affordance */}
              <span
                aria-hidden="true"
                className="absolute bottom-1.5 end-1.5 flex h-6 w-6 items-center justify-center rounded-full bg-[color:var(--surface)] shadow-[var(--shadow-sm)]"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
                  <circle cx="12" cy="13" r="4" />
                </svg>
              </span>
            </button>

            {/* Contextual action buttons */}
            <div className="flex flex-1 flex-col gap-2.5">
              {stage === "previewing" ? (
                /* Crop confirmed — Upload · Adjust crop · Cancel */
                <>
                  <p className="truncate text-xs text-[color:var(--text-muted)]" title={selectedFile?.name}>
                    {selectedFile?.name}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      type="button"
                      onClick={() => void handleUpload()}
                      disabled={isBusy || !preparedLogo}
                      variant="primary"
                      className="h-9 rounded-lg px-4 text-xs font-semibold disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {isBusy ? t(keys.common.loading) : t(keys.common.upload)}
                    </Button>
                    <Button
                      type="button"
                      onClick={handleBackToCrop}
                      disabled={isBusy}
                      variant="outline"
                      className="h-9 rounded-lg px-4 text-xs font-semibold disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {t(keys.seller.storeLogoCropAdjust)}
                    </Button>
                    <Button
                      type="button"
                      onClick={resetAll}
                      disabled={isBusy}
                      variant="outline"
                      className="h-9 rounded-lg px-4 text-xs font-semibold disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {t(keys.common.cancel)}
                    </Button>
                  </div>
                </>
              ) : (
                /* Idle — Replace (if logo exists) + Remove */
                <div className="flex flex-wrap gap-2">
                  <Button
                    type="button"
                    onClick={() => inputRef.current?.click()}
                    disabled={isBusy}
                    variant="outline"
                    className="h-9 rounded-lg px-4 text-xs font-semibold disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {logoUrl ? t(keys.seller.storeLogoReplace) : t(keys.common.upload)}
                  </Button>
                  {logoUrl ? (
                    <Button
                      type="button"
                      onClick={() => void handleRemove()}
                      disabled={isBusy}
                      variant="outline"
                      className="h-9 rounded-lg px-4 text-xs font-semibold text-[color:var(--status-error)] disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {t(keys.seller.storeLogoRemove)}
                    </Button>
                  ) : null}
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
